'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AOS from 'aos';
import 'aos/dist/aos.css';
import styles from './RdvSlotDetail.module.scss';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { trackMetaBookingEntry } from '@/src/lib/meta/meta-pixel';

type RdvDaySlot = {
    startTime: string;
    endTime: string;
    activityIds: string[];
    capacity: number;
    pricePerPersonCents: number;
    imageUrl: string;
};

type RdvDayResponse = {
    date: string;
    slots: RdvDaySlot[];
};

type RdvActivity = {
    id: string;
    label?: string;
    title?: string;
    description?: string;
    durationMin?: number;
    imageUrl?: string | null;
};

type RdvActivitiesResponse = {
    items: RdvActivity[];
};

type ActivityOverride = {
    eyebrow?: string;
    title?: string;
    intro?: string;
    paragraphs?: string[];
    included?: string[];
    usefulInfo?: string[];
    highlightTitle?: string;
    highlightText?: string;
    imageUrl?: string;
    imageAlt?: string;
    ctaLabel?: string;
    badge?: string;
};

export type RdvSlotDetailSlice = {
    type: 'rdv-slot-detail';
    eyebrow?: string;
    title?: string;
    subtitle?: string;
    bookingHref?: string;
    backHref?: string;
    backLabel?: string;
    slotTitleLabel?: string;
    activitiesTitle?: string;
    includedLabel?: string;
    usefulInfoTitle?: string;
    usefulInfoBookingLinkText?: string;
    periodMorningLabel?: string;
    periodAfternoonLabel?: string;
    dateLabel?: string;
    timeLabel?: string;
    capacityLabel?: string;
    priceLabel?: string;
    durationLabel?: string;
    emptyText?: string;
    errorText?: string;
    loadingText?: string;
    missingParamsText?: string;
    notFoundText?: string;
    reserveLabel?: string;
    placeholderImage?: string;
    contentOverrides?: Record<string, ActivityOverride>;
};

type Props = {
    slice: RdvSlotDetailSlice;
    lang?: 'fr' | 'en';
};

function slugify(value?: string) {
    return (value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

function cleanText(value?: string) {
    return (value ?? '').replace(/\s+/g, ' ').trim();
}

function formatDateFr(ymd?: string) {
    if (!ymd) return '';
    const [year, month, day] = ymd.split('-').map(Number);
    if (!year || !month || !day) return ymd;

    const date = new Date(year, month - 1, day);

    return new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(date);
}

function formatPriceFromCents(cents?: number | null) {
    if (typeof cents !== 'number' || Number.isNaN(cents)) return null;
    return `${Math.round(cents / 100)}€ / pers`;
}

function parseHour(time?: string) {
    if (!time) return 0;
    const [h] = time.split(':').map(Number);
    return Number.isFinite(h) ? h : 0;
}

function getPeriodLabel(
    startTime: string,
    morningLabel: string,
    afternoonLabel: string,
) {
    const hour = parseHour(startTime);
    return hour < 13 ? morningLabel : afternoonLabel;
}

function getOverrideForActivity(
    overrides: Record<string, ActivityOverride> | undefined,
    activity: RdvActivity,
) {
    if (!overrides) return undefined;

    const idMatch = overrides[activity.id];
    if (idMatch) return idMatch;

    const title = activity.title || activity.label || '';
    const slugMatch = overrides[slugify(title)];
    if (slugMatch) return slugMatch;

    return undefined;
}

export default function RdvSlotDetail({ slice, lang = 'fr' }: Props) {
    const dictionary = useDictionary();
    const searchParams = useSearchParams();

    const date = searchParams.get('date') || '';
    const start = searchParams.get('start') || '';

    const [slot, setSlot] = useState<RdvDaySlot | null>(null);
    const [activities, setActivities] = useState<RdvActivity[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const labels = useMemo(
        () => ({
            eyebrow: slice.eyebrow ?? 'Rendez-vous créatif',
            title: slice.title ?? 'Votre créneau',
            subtitle:
                slice.subtitle ??
                'Découvrez les activités proposées sur ce créneau et réservez votre place.',
            bookingHref: slice.bookingHref ?? '/booking/rdv',
            backHref: slice.backHref ?? '/rendez-vous',
            backLabel: slice.backLabel ?? 'Retour aux rendez-vous',
            slotTitleLabel: slice.slotTitleLabel ?? 'Créneau sélectionné',
            activitiesTitle: slice.activitiesTitle ?? 'Activités proposées',
            includedLabel: slice.includedLabel ?? 'Inclus',
            usefulInfoTitle: slice.usefulInfoTitle ?? 'Infos utiles',
            usefulInfoBookingLinkText:
                slice.usefulInfoBookingLinkText ??
                'Ce créneau est réservable directement depuis la page de réservation.',
            periodMorningLabel: slice.periodMorningLabel ?? 'Matin',
            periodAfternoonLabel: slice.periodAfternoonLabel ?? 'Après-midi',
            dateLabel: slice.dateLabel ?? 'Date',
            timeLabel: slice.timeLabel ?? 'Horaire',
            capacityLabel: slice.capacityLabel ?? 'Places',
            priceLabel: slice.priceLabel ?? 'Tarif',
            durationLabel: slice.durationLabel ?? 'Durée',
            emptyText: slice.emptyText ?? 'Aucune activité disponible pour ce créneau.',
            errorText:
                slice.errorText ??
                'Impossible de charger les informations du créneau pour le moment.',
            loadingText: slice.loadingText ?? 'Chargement du créneau…',
            missingParamsText:
                slice.missingParamsText ??
                'Paramètres manquants. Merci de préciser une date et une heure de début.',
            notFoundText:
                slice.notFoundText ??
                'Aucun créneau correspondant n’a été trouvé pour cette date et cette heure.',
            reserveLabel: slice.reserveLabel ?? dictionary?.general?.book ?? 'Réserver',
            placeholderImage:
                slice.placeholderImage ??
                dictionary?.general?.imageDefault ??
                '/images/placeholder.jpg',
        }),
        [slice, dictionary],
    );

    useEffect(() => {
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isMobile = window.innerWidth < 1024;

        if (reducedMotion || isMobile) {
            return;
        }

        AOS.init({ once: true, duration: 850, easing: 'ease-out-cubic', offset: 80 });
        AOS.refresh();
    }, []);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!date || !start) {
                setLoading(false);
                setHasError(false);
                setSlot(null);
                setActivities([]);
                return;
            }

            try {
                setLoading(true);
                setHasError(false);

                const [dayRes, activitiesRes] = await Promise.all([
                    fetch(`/api/booking/rdv/days/${date}`, {
                        method: 'GET',
                        cache: 'no-store',
                    }),
                    fetch(`/api/booking/rdv/activites`, {
                        method: 'GET',
                        cache: 'no-store',
                    }),
                ]);

                if (!dayRes.ok || !activitiesRes.ok) {
                    throw new Error('Failed to load RDV slot detail');
                }

                const dayData: RdvDayResponse = await dayRes.json();
                const activitiesData: RdvActivitiesResponse = await activitiesRes.json();

                const foundSlot =
                    (dayData.slots ?? []).find((item) => item.startTime === start) ?? null;

                if (cancelled) return;

                setSlot(foundSlot);

                if (!foundSlot) {
                    setActivities([]);
                    return;
                }

                const activityMap = new Map(
                    (activitiesData.items ?? []).map((item) => [String(item.id), item]),
                );

                const expandedActivities = foundSlot.activityIds
                    .map((id) => activityMap.get(String(id)))
                    .filter(Boolean) as RdvActivity[];

                setActivities(expandedActivities);
            } catch (error) {
                console.error('RdvSlotDetail load error:', error);
                if (!cancelled) {
                    setHasError(true);
                    setSlot(null);
                    setActivities([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [date, start]);

    const periodLabel = slot
        ? getPeriodLabel(
            slot.startTime,
            labels.periodMorningLabel,
            labels.periodAfternoonLabel,
        )
        : '';

    const bookingLink = `${lang ? `/${lang}` : ''}${labels.bookingHref}`;

    if (loading) {
        return (
            <section className={styles.section}>
                <div className={styles.stateBlock}>
                    <p>{labels.loadingText}</p>
                </div>
            </section>
        );
    }

    if (!date || !start) {
        return (
            <section className={styles.section}>
                <div className={styles.stateBlock}>
                    <p>{labels.missingParamsText}</p>
                </div>
            </section>
        );
    }

    if (hasError) {
        return (
            <section className={styles.section}>
                <div className={styles.stateBlock}>
                    <p>{labels.errorText}</p>
                </div>
            </section>
        );
    }

    if (!slot) {
        return (
            <section className={styles.section}>
                <div className={styles.stateBlock}>
                    <p>{labels.notFoundText}</p>
                </div>
            </section>
        );
    }

    return (
        <section className={styles.section}>
            <div className={styles.container}>
                <div className={styles.hero} data-aos="fade-up">
                    <div className={styles.heroImageWrap}>
                        <Image
                            src={slot.imageUrl || labels.placeholderImage}
                            alt={labels.slotTitleLabel}
                            fill
                            className={styles.heroImage}
                            sizes="(max-width: 1000px) 100vw, 54vw"
                        />
                    </div>

                    <div className={styles.heroContent}>
                        <p className={styles.eyebrow}>{labels.eyebrow}</p>
                        <h1 className={styles.title}>{labels.title} {periodLabel}</h1>
                        <p className={styles.subtitle}>{labels.subtitle}</p>

                        <div className={styles.metaGrid}>
                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>{labels.dateLabel}</span>
                                <span className={styles.metaValue}>{formatDateFr(date)}</span>
                            </div>

                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>{labels.timeLabel}</span>
                                <span className={styles.metaValue}>
                                    {slot.startTime} – {slot.endTime}
                                </span>
                            </div>

                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>Période</span>
                                <span className={styles.metaValue}>{periodLabel}</span>
                            </div>

                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>{labels.capacityLabel}</span>
                                <span className={styles.metaValue}>{slot.capacity}</span>
                            </div>

                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>{labels.priceLabel}</span>
                                <span className={styles.metaValue}>
                                    {formatPriceFromCents(slot.pricePerPersonCents) || '—'}
                                </span>
                            </div>

                            <div className={styles.metaItem}>
                                <span className={styles.metaLabel}>{labels.activitiesTitle}</span>
                                <span className={styles.metaValue}>{activities.length}</span>
                            </div>
                        </div>

                        <div className={styles.actions}>
                            <Link
                                href={bookingLink}
                                className={styles.primaryBtn}
                                onClick={() => trackMetaBookingEntry(bookingLink)}
                            >
                                {labels.reserveLabel}
                            </Link>

                            <Link href={`${lang ? `/${lang}` : ''}${labels.backHref}`} className={styles.secondaryBtn}>
                                {labels.backLabel}
                            </Link>
                        </div>
                    </div>
                </div>

                <div className={styles.activitiesWrap}>
                    <div className={styles.sectionHead} data-aos="fade-up">
                        <p className={styles.sectionKicker}>{labels.slotTitleLabel}</p>
                        <h2 className={styles.sectionTitle}>{labels.activitiesTitle}</h2>
                    </div>

                    {!activities.length ? (
                        <div className={styles.stateBlock}>
                            <p>{labels.emptyText}</p>
                        </div>
                    ) : (
                        <div className={styles.activitiesGrid}>
                            {activities.map((activity, index) => {
                                const override = getOverrideForActivity(slice.contentOverrides, activity);
                                const title = override?.title || activity.title || activity.label || 'Activité';
                                const description = cleanText(activity.description);
                                const paragraphs =
                                    override?.paragraphs && override.paragraphs.length
                                        ? override.paragraphs
                                        : description
                                            ? [description]
                                            : [];
                                const intro = override?.intro || description;
                                const imageUrl =
                                    override?.imageUrl || activity.imageUrl || slot.imageUrl || labels.placeholderImage;

                                return (
                                    <article
                                        key={activity.id}
                                        className={styles.activityCard}
                                        data-aos="fade-up"
                                        data-aos-delay={Math.min(index * 80, 240)}
                                    >
                                        <div className={styles.activityImageWrap}>
                                            <Image
                                                src={imageUrl}
                                                alt={override?.imageAlt || title}
                                                fill
                                                className={styles.activityImage}
                                                sizes="(max-width: 720px) 100vw, 40vw"
                                            />
                                        </div>

                                        <div className={styles.activityBody}>
                                            <p className={styles.activityEyebrow}>
                                                {override?.badge || override?.eyebrow || periodLabel}
                                            </p>

                                            <h3 className={styles.activityTitle}>{title}</h3>

                                            <div className={styles.activityMetaRow}>
                                                <span className={styles.infoPill}>
                                                    {labels.durationLabel}:{' '}
                                                    {typeof activity.durationMin === 'number'
                                                        ? `${activity.durationMin} min`
                                                        : '—'}
                                                </span>
                                                <span className={styles.infoPill}>{periodLabel}</span>
                                            </div>

                                            {(override?.highlightTitle || override?.highlightText || intro) && (
                                                <div className={styles.highlightBox}>
                                                    <span className={styles.highlightLabel}>
                                                        {override?.highlightTitle || 'Description'}
                                                    </span>
                                                    <p>{override?.highlightText || intro}</p>
                                                </div>
                                            )}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}